import { type File } from '@littlebird/sdk';

import {
  MAX_HTML_PREVIEW_BYTES,
  MAX_OFFICE_PREVIEW_BYTES,
  MAX_PDF_CARD_PREVIEW_BYTES,
  MAX_TEXT_PREVIEW_BYTES,
} from '@/utils/constants';
import {
  isArchiveFile,
  isCanvasFile,
  isExcelWorkbookFile,
  isHtmlFile,
  isImageFile,
  isMarkdownFile,
  isPdfFile,
  isPresentationFile,
  isSpreadsheetFile,
  isTextPreviewable,
  isWordDocFile,
} from '@/utils/files';

import type { ArtifactPaperSkeleton } from './ArtifactPaper';

export type ArtifactRenderer =
  | 'image'
  | 'canvas'
  | 'html'
  | 'pdf'
  | 'docx'
  | 'xlsx'
  | 'pptx'
  | 'markdown'
  | 'text'
  | 'none';

export type OfficeRenderer = 'docx' | 'xlsx' | 'pptx';

const OFFICE_RENDERERS: readonly ArtifactRenderer[] = ['docx', 'xlsx', 'pptx'];

export const isOfficeRenderer = (
  renderer: ArtifactRenderer
): renderer is OfficeRenderer => OFFICE_RENDERERS.includes(renderer);

type RendererMatch = [
  ArtifactRenderer,
  (contentType: string | undefined, filename: string) => boolean,
];

const RENDERERS: RendererMatch[] = [
  ['image', isImageFile],
  ['canvas', isCanvasFile],
  ['html', isHtmlFile],
  ['pdf', isPdfFile],
  ['docx', isWordDocFile],
  ['xlsx', isExcelWorkbookFile],
  ['pptx', isPresentationFile],
  ['markdown', isMarkdownFile],
  ['text', isTextPreviewable],
];

export const artifactRenderer = (file: File): ArtifactRenderer =>
  RENDERERS.find(([, matches]) =>
    matches(file.contentType, file.filename)
  )?.[0] ?? 'none';

export const getFileVersionKey = (file: { id: string; updatedAt: Date }) =>
  `${file.id}:${file.updatedAt.getTime()}`;

export type ArtifactLoadMode = 'text' | 'binary' | 'url' | 'none';

export type ArtifactSurface = 'card' | 'preview';

export type ArtifactPolicy = {
  renderer: ArtifactRenderer;
  paper?: ArtifactPaperSkeleton;
  byteCap: number;
  card: ArtifactLoadMode;
  preview: ArtifactLoadMode;
};

export const artifactPolicy = (file: File | undefined): ArtifactPolicy => {
  if (!file)
    return { renderer: 'none', byteCap: 0, card: 'none', preview: 'none' };
  const renderer = artifactRenderer(file);
  const spreadsheetPaper = isSpreadsheetFile(file.contentType, file.filename)
    ? ('spreadsheet' as const)
    : undefined;

  switch (renderer) {
    case 'canvas':
    case 'html':
      return {
        renderer,
        paper: 'document',
        byteCap: MAX_HTML_PREVIEW_BYTES,
        card: 'text',
        preview: 'text',
      };
    case 'pdf':
      return {
        renderer,
        paper: 'document',
        byteCap: 0,
        card: file.size > MAX_PDF_CARD_PREVIEW_BYTES ? 'none' : 'url',
        preview: 'url',
      };
    case 'image':
      return {
        renderer,
        paper: 'document',
        byteCap: 0,
        card: 'url',
        preview: 'url',
      };
    case 'docx':
    case 'xlsx':
    case 'pptx':
      return {
        renderer,
        paper: renderer === 'xlsx' ? 'spreadsheet' : 'document',
        byteCap: MAX_OFFICE_PREVIEW_BYTES,
        card: 'none',
        preview: 'binary',
      };
    case 'markdown':
    case 'text':
      return {
        renderer,
        paper: spreadsheetPaper ?? 'text',
        byteCap: MAX_TEXT_PREVIEW_BYTES,
        card: 'none',
        preview: 'text',
      };
    case 'none':
      return {
        renderer,
        paper:
          spreadsheetPaper ??
          (isArchiveFile(file.contentType, file.filename)
            ? 'archive'
            : undefined),
        byteCap: 0,
        card: 'none',
        preview: 'none',
      };
  }
};

export const paperShowsFile = (policy: ArtifactPolicy) =>
  policy.card !== 'none';

export const canPreview = (policy: ArtifactPolicy) => policy.preview !== 'none';
